import type { OrderStatus, VisitOutcome } from './types';

/**
 * Maps a technician-reported VisitOutcome to the resulting OrderStatus.
 *
 * Introduced for P0-2 in ROADMAP.md: previously, every completion call
 * collapsed the order into 'done' regardless of outcome — which hid
 * parts_required / no_access / waiting_approval cases inside analytics
 * and SLA reports. Now exceptional outcomes land on dedicated statuses
 * so dispatcher and admin can act on them in the attention queue and
 * filter them out of "actually completed work" metrics.
 *
 * Visit-success outcomes ('completed', 'partially_completed') still
 * resolve to 'done'; for partial completions the precise distinction
 * is preserved in result_summary.visit_outcome on the order itself.
 *
 * 1.19.4 — Эта функция возвращает ФИНАЛЬНЫЙ статус (target). Между
 * нажатием «Завершить» и появлением финального статуса заявка живёт
 * в transit-состоянии 'pending_completion' (см. closeVisit pipeline в
 * visitStore + outboxProcessor). Сама эта функция о transit ничего не
 * знает — она маппит outcome → target. Transit оформляется протоколом
 * закрытия в visitStore.
 */
export function statusForVisitOutcome(outcome: VisitOutcome | null | undefined): OrderStatus {
  switch (outcome) {
    case 'parts_required':
      return 'awaiting_parts';
    case 'waiting_approval':
      return 'awaiting_approval';
    case 'follow_up_required':
      return 'awaiting_follow_up';
    case 'client_rescheduled':
      return 'rescheduled';
    case 'no_access':
      return 'failed_no_access';
    // 1.12.1 — abortVisit пишет visit_outcome='failed'. Без явной ветки
    // default возвращал бы 'done', что неправильно для прерванного визита.
    case 'failed':
      return 'failed';
    case 'completed':
    case 'partially_completed':
    case null:
    case undefined:
    default:
      return 'done';
  }
}

/**
 * Statuses an order can carry while it is still "in motion" — i.e. the
 * dispatcher or technician will continue to act on it. Used to drive
 * the dispatcher's active queue, technician's day list, team-load math
 * in useTeam(), and the admin attention panel.
 *
 * 1.19.4 — 'pending_completion' входит сюда: визит формально не закрыт
 * (нет visit_results), и оба контура (техник + диспетчер) должны видеть
 * заявку как «ещё в работе, идёт финализация».
 */
export const ACTIVE_ORDER_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'new',
  'assigned',
  'in_progress',
  'pending_completion',
]);

/**
 * Statuses that are "closed by a visit, but not actually finished work".
 * These are the outcomes of P0-2: the visit is over, but the dispatcher
 * still owes follow-up action (order parts, re-confirm a slot, reschedule,
 * push approval). They must show up in attention queues, NOT in the
 * "completed" KPI bucket.
 */
export const PENDING_FOLLOW_UP_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'awaiting_parts',
  'awaiting_approval',
  'awaiting_follow_up',
  'rescheduled',
]);

/**
 * Statuses that count as "the work itself was completed" for KPI/payroll.
 * 'failed' and 'failed_no_access' are explicitly NOT here.
 *
 * 1.19.4 — 'pending_completion' тут НЕ участвует. Пока visit_results не
 * записаны и финальный статус не выставлен — заявка не идёт в KPI.
 */
export const COMPLETED_STATUSES: ReadonlySet<OrderStatus> = new Set(['done']);

/**
 * Statuses that count as "terminal failure of this visit" — work was
 * not done and there is no follow-up automatically planned by the system.
 */
export const FAILED_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'failed',
  'failed_no_access',
  'cancelled',
]);

/**
 * Any status that means "the technician already closed this visit" — be it
 * a clean 'done', a hard failure, or one of the follow-up states.
 * Used by sync code to avoid blocking on unsent photos for visits the
 * technician has already wrapped up, and by mergeRemoteWithLocal() to detect
 * that local progress is fresher than the server copy.
 *
 * 1.19.4 — 'pending_completion' тут НЕ участвует. Это transit-состояние:
 * формально визит ещё не закрыт, finalize_visit RPC ещё не выполнен,
 * visit_results не существует. Outbox flow ОБЯЗАН обрабатывать заявки в
 * этом статусе как «незакрытые» — иначе reconcileTerminalOutbox может
 * преждевременно удалить outbox items до их доставки на сервер.
 */
export const VISIT_CLOSED_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'done',
  'failed',
  'cancelled',
  'awaiting_parts',
  'awaiting_approval',
  'awaiting_follow_up',
  'rescheduled',
  'failed_no_access',
]);

/**
 * 1.19.4 — Transit-статусы: lifecycle ещё в процессе финализации.
 * Может быть только 'pending_completion' сейчас. Выделено в отдельный
 * Set чтобы код мог явно отличать «активная заявка» (новая, назначенная,
 * в работе) от «заявка финализируется» (техник закрыл, ждём сети).
 *
 * Использование:
 *   • UI показывает специальный маркер «Закрывается, синхронизация».
 *   • Outbox processor НЕ применяет terminal-cleanup до выхода из этого
 *     состояния.
 *   • mergeRemoteWithLocal даёт приоритет local pending_completion над
 *     remote in_progress (техник видел финал, синхронизация догоняет).
 */
export const FINALIZING_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'pending_completion',
]);

/**
 * 1.19.4 — Финальные статусы lifecycle'а заявки. После попадания сюда
 * откатиться обратно в активные или transit НЕЛЬЗЯ — это инвариант I5
 * закрытия визита (см. types.ts комментарий к OrderStatus).
 *
 * Используется для:
 *   • Серверной защиты в finalize_visit_rpc от повторного выполнения.
 *   • Клиентских проверок «можно ли менять состояние заявки».
 */
export const FINAL_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'done',
  'failed',
  'cancelled',
  'awaiting_parts',
  'awaiting_approval',
  'awaiting_follow_up',
  'rescheduled',
  'failed_no_access',
]);

/**
 * True if the status indicates a visit that has been closed in any way.
 *
 * 1.19.4 — Для 'pending_completion' возвращает FALSE. Визит формально
 * не закрыт пока finalize_visit_rpc не выполнен на сервере.
 */
export function isVisitClosedStatus(status: OrderStatus): boolean {
  return VISIT_CLOSED_STATUSES.has(status);
}

/**
 * True if the order is still actively in motion (not closed in any sense).
 *
 * 1.19.4 — 'pending_completion' попадает сюда: с точки зрения «требует
 * системного внимания» — да, заявка ещё работает (outbox flow). UI
 * показывает её в активных списках с дополнительным маркером.
 */
export function isActiveOrderStatus(status: OrderStatus): boolean {
  return ACTIVE_ORDER_STATUSES.has(status);
}

/**
 * True if the order is "visit-closed but still needs dispatcher follow-up".
 * Used by the admin attention panel and dispatcher's pending lists.
 */
export function isPendingFollowUpStatus(status: OrderStatus): boolean {
  return PENDING_FOLLOW_UP_STATUSES.has(status);
}

/**
 * 1.19.4 — True если заявка находится в transit-состоянии финализации.
 * Используется UI для специальной отрисовки («Закрывается...») и outbox
 * processor для приоритизации.
 */
export function isFinalizingStatus(status: OrderStatus): boolean {
  return FINALIZING_STATUSES.has(status);
}

/**
 * 1.19.4 — True если заявка находится в финальном (терминальном)
 * состоянии lifecycle'а. После попадания в финал откат запрещён.
 */
export function isFinalStatus(status: OrderStatus): boolean {
  return FINAL_STATUSES.has(status);
}
